<template>
  <app-shell breadcrumbs="Home , Payments">
    <div class="payments__container">
      <div class="row no-gutters mt-4">
        <div class="col-12">
          <!-- Payments Chart -->
          <div class="payments__chart-container">
            <comparison-board :data="comparisonData" @filter-chart="rebuildComparisonChart"></comparison-board>
          </div>
          <!-- End of the payments chart -->
        </div>
      </div>

      <div class="row no-gutters mt-4">
        <div class="col-12">
          <!-- Payment data table -->
          <div class="payments__data-table-container">
            <data-table :actions="actions" :buttons="buttons" :column-configuration="columns" :filters="filters"
                        :selected-row="selectedRow" :page="page" :page-limit="pageLimit" :rows="rows" @filter="rebuildTable" @next-page="nextPage"
                        @prev-page="prevPage" @show-visit="showVisit" @simulate-payment="launchSimulator" @view="selectRow">
              <div class="ml-data-table__placeholder">
                <i class="fas fa-credit-card fa-3x"></i>
                <h5 class="my-3">You do not have any payments</h5>
              </div>
            </data-table>
          </div>
          <!-- End of the payments data table -->
        </div>
      </div>

      <!-- Simulate a payment -->
      <modal action="Pay" :name="`simulate-payment`" :no-icon="false" :title="`<b>Simulate</b> a payment`"
             @cancel="cancelSimulator" @save="simulatePayment">
        <div class="form-group">
          <label class="control-label" for="phone_number">Mobile number</label>
          <input type="text"
                 class="form-control"
                 name="phone_number"
                 id="phone_number"
                 v-model="simulatedPayment.MSISDN"
                 v-validate="'required|phone_number'"
          >
          <span v-show="errors.has('phone_number')" class="help is-danger">
            {{ errors.first('phone_number') }}
          </span>
        </div>

        <div class="form-group">
          <label class="control-label" for="amount">Amount</label>
          <input type="number"
                 class="form-control"
                 name="amount"
                 id="amount"
                 v-model="simulatedPayment.chargeAmount"
                 v-validate="'required|min_value:1'"
          >
          <span v-show="errors.has('amount')" class="help is-danger">
            {{ errors.first('amount') }}
          </span>
        </div>

        <div class="form-group">
          <label class="control-label" for="account_number">Account number</label>
          <input type="text"
                 class="form-control"
                 name="account_number"
                 id="account_number"
                 v-model="simulatedPayment.accountNumber"
                 v-validate="'required'"
          >
          <span v-show="errors.has('account_number')" class="help is-danger">
            {{ errors.first('account_number') }}
          </span>
        </div>

        <div class="form-group">
          <label class="control-label" for="payment_option">Payment option</label>
          <select id="payment_option" name="payment_option" class="form-control"
                  v-model="simulatedPayment.paymentOption" v-validate="'required'">
            <option v-for="client in _preparePayerClients(meta.payerClients)" :key="client.label" :value="client"> {{ client.label }} </option>
          </select>
          <span v-show="errors.has('payment_option')" class="help is-danger">
            {{ errors.first('payment_option') }}
          </span>
        </div>
      </modal>
      <!-- End of simulate payment -->
    </div>
  </app-shell>
</template>

<script>
  import _ from 'lodash'
  import Shell from '@/components/Shell'
  import Modal from '@/components/Modal'
  import { Validator } from 'vee-validate'
  import PhoneNumber from 'awesome-phonenumber'
  import DataTable from '@/components/DataTable'
  import Payments from '@/api/services/payments'
  import DataTableMixin from '@/mixins/DataTable'
  import ComparisonDataMixin from '@/mixins/ComparisonData'
  import ComparisonBoard from '@/components/ComparisonBoard'
  import PaymentSimulatorMixin from '@/mixins/PaymentSimulator'

  const logInfo = !!(process.env.NODE_ENV !== 'production')

  export default {
    components: {
      'modal': Modal,
      'app-shell': Shell,
      'data-table': DataTable,
      'comparison-board': ComparisonBoard,
    },
    computed: {
      filters () {
        return [
          {
            label: 'Account number',
            type: 'string',
            key: 'accountNumber'
          },
          {
            label: 'Amount',
            type: 'number',
            key: 'amountPaid'
          },
          {
            label: 'Visit ID',
            type: 'string',
            key: 'checkoutRequestID',
            ...this._passedFilters('checkoutRequestID')
          },
          {
            label: 'Country',
            type: 'list',
            key: 'countryCode',
            list: this._prepareCountries(this.meta.countries)
          },
          {
            label: 'Currency',
            type: 'list',
            key: 'currencyCode',
            list: this._prepareCurrencies(this.meta.currencies)
          },
          {
            label: 'Date',
            type: 'date',
            key: 'date'
          },
          {
            label: 'Mobile number',
            type: 'string',
            key: 'MSISDN'
          },
          {
            label: 'Payer client',
            type: 'list',
            key: 'payerClientCode',
            list: this._preparePayerClients(this.meta.payerClients)
          },
          {
            label: 'Status',
            type: 'list',
            key: 'hubOverallStatus',
            list: this._prepareStatuses(this.meta.statusCodes)
          }
        ]
      }
    },
    data () {
      return {
        actions: [
          {
            name: 'View',
            event: 'view'
          },
          {
            name: 'Visit',
            event: 'show-visit'
          }
        ],
        buttons: [
          {
            name: 'Simulate',
            icon: 'plus',
            event: 'simulate-payment'
          }
        ],
        columns: {
          MSISDN: {
            filter: 'phoneNumber',
            label: 'Mobile Number'
          },
          accountNumber: {
            label: 'Account Number'
          },
          amountPaid: {
            float: 'left',
            filter: 'money',
            label: 'Amount',
            highlight: true
          },
          checkoutRequestID: {
            label: 'Visit ID'
          },
          currencyCode: {
            float: 'right',
            highlight: true,
            label: ''
          },
          customerName: {
            label: 'Customer Name',
            collapsed: true
          },
          ownerClientCode: {
            hide: true
          },
          ownerClientDisplayName: {
            hide: true
          },
          ownerClientName: {
            hide: true
          },
          payerClientCode: {
            hide: true
          },
          payerClientDisplayName: {
            label: 'Payment Mode',
            collapsed: true
          },
          payerClientName: {
            hide: true
          },
          paymentReceivedDate: {
            float: 'right',
            collapsed: true,
            type: 'date'
          },
          payerTransactionID: {
            label: 'Payer Transaction ID',
            collapsed: true
          },
          paymentDate: {
            float: 'right',
            label: 'Date',
            type: 'date'
          },
          receiverNarration: {
            collapsed: true,
            label: 'Description'
          },
          merchantReceipt: {
            collapsed: true,
            label: 'Receipt number'
          },
          datePaymentAcknowledged: {
            collapsed: true,
            float: 'right',
            label: 'Payment acknowledged on',
            type: 'date'
          }
        }
      }
    },
    methods: {
      _formatPayments (payments) {
        let structuredPayments = []
        _.forIn(payments, payment => {
          structuredPayments.push({
            checkoutRequestID: payment.checkoutRequestID,
            currencyCode: payment.currencyCode,
            amountPaid: payment.amountPaid,
            MSISDN: payment.MSISDN,
            accountNumber: payment.accountNumber,
            status: payment.statusCodeDesc,
            paymentDate: payment.dateCreated,
            receiverNarration: payment.receiverNarration,
            customerName: payment.customerName,
            merchantReceipt: payment.merchantReceipt,
            payerTransactionID: payment.payerTransactionID,
            paymentReceivedDate: payment.datePaymentReceived,
            payerClientDisplayName: payment.payerClientDisplayName,
            datePaymentAcknowledged: payment.datePaymentAcknowledged,
            payerClientCode: payment.payerClientCode,
            ownerClientCode: payment.ownerClientCode,
            ownerClientDisplayName: payment.ownerClientDisplayName,
            ownerClientName: payment.ownerClientName,
            payerClientName: payment.payerClientName
          })
        })
        return structuredPayments
      },
      _passedFilters () {
        logInfo && console.log('What was the filter passed ?', this.$route.query.filter)
        if (this.$route.query.filter !== undefined) {
          return {
            mode: 'like',
            selected: true,
            value: this.$route.query.filter
          }
        }
        return {}
      },
      attachCustomValidators () {
        Validator.extend('phone_number', {
          getMessage: field => `The phone number provided is not a valid phone number.`,
          validate: value => new Promise((resolve) => {
            let isPhoneNumberValid = PhoneNumber(`+${value}`).isValid()
            resolve({
              valid: isPhoneNumberValid
            })
          })
        })
      },
      showVisit (payment) {
        logInfo && console.log('Select the visit for the payment: ', payment)
        this.$router.push('/visits?filter=' + payment.checkoutRequestID)
      },
      rebuildComparisonChart (filters) {
        logInfo && console.log('Rebuilding the comparison chart....', filters)
        Payments.whereService({ serviceCode: this.currentService.serviceCode }).fetchComparisonData({ filters })
          .then(response => {
            logInfo && console.log('Comparison chart data received => ', response)
            this.comparisonData = response
          })
          .catch(reason => {
            logInfo && console.log('Failed to rebuild the comparison chart => ', reason)
          })
      },
      rebuildTable (filters, page = null, rebuildFilters = false) {
        logInfo && console.log('Filters passed to rebuild the table are: ', filters)
        let formattedFilters = this._extractFormattedFilters(filters)
        logInfo && console.log('Filters from the data table', formattedFilters)
        Payments.whereService({ serviceCode: this.currentService.serviceCode }).fetchAll({ page: (page === null) ? this.page : page, filters: formattedFilters })
          .then(response => {
            logInfo && console.log('Payments fetched from the engine => ', response)
            this.rows = this._formatPayments(response.DATA.payments)
            this.page = response.DATA.page
            this.pageLimit = response.DATA.pageLimit
            if (rebuildFilters) {
              this.meta = response.DATA.metaData
            }
          })
          .catch(reason => {
            logInfo && console.log('Payments failed from the engine => ', reason)
          })
      }
    },
    mixins: [ComparisonDataMixin, DataTableMixin, PaymentSimulatorMixin],
    mounted () {
      this.attachCustomValidators()
      this.rebuildTable(this.filters, 0, true)
    },
    name: 'Payments'
  }
</script>

<style lang="scss">
  @import "../styles/theme";
</style>



// WEBPACK FOOTER //
// src/views/Payments.vue